import css from './TextInput.module.css';
import { useState } from 'react';
import Visibility from '../Visibility/Visibility';
import StatusText from '../StatusText/StatusText';
import { FiEye, FiEyeOff } from 'react-icons/fi';

type TextInputProps = {
  label?: string;
  placeholder?: string;
  type: string;
  name: string;
  value: string;
  valid: boolean;
  statusText?: string;
  disabled?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
};

const TextInput = ({
  label,
  placeholder,
  type: initialType,
  name,
  value,
  valid,
  statusText,
  disabled = false,
  onChange,
}: TextInputProps) => {
  const [pure, setPure] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const showError = !valid && !pure;

  const inputClass = `${showError ? css.error : ''} ${disabled ? css.disabled : ''}`;
  const wrapperClass = `${css.inputWrapper} ${showError ? css.error : ''} ${disabled ? css.disabled : ''}`;

  const type = initialType === 'password' ? (showPassword ? 'text' : 'password') : initialType;

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    setPure(false);
    if (onChange !== undefined) {
      onChange(event);
    }
  };

  const onFocusHandler = () => {
    if (disabled) return;
    setIsFocused(true);
  };

  const onBlurHandler = () => {
    if (disabled) return;
    setIsFocused(false);
  };

  const togglePassword = () => {
    if (disabled) return;
    setShowPassword(prev => !prev);
  };

  if (statusText === undefined) {
    statusText = 'This field is required';
  }

  return (
    <div className={css.textInput}>
      <Visibility visible={label !== undefined}>
        <label htmlFor={name} className={disabled ? css.disabledLabel : ''}>{label}</label>
      </Visibility>
      <div className={wrapperClass}>
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChangeHandler}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          placeholder={placeholder}
          className={inputClass}
          disabled={disabled}
          autoComplete={type === 'password' ? 'current-password' : 'off'}
        />
        {initialType === 'password' && (
          <button
            type="button"
            className={`${css.icon} ${disabled ? css.disabled : ''}`}
            onClick={togglePassword}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={disabled}
          >
            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
          </button>
        )}
      </div>
      <Visibility visible={showError && !disabled}>
        <div className={css.errorMessage}>
          <StatusText text={statusText} tiny={true} error={true} />
        </div>
      </Visibility>
    </div>
  );
};

export default TextInput;